import { useTranslation } from "react-i18next";
import { useUserProfile } from "../../../hooks/useUserProfile";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Indicator, Loading, Modal, Input, Textarea } from "react-daisyui";
import { Link } from "react-router-dom";
import { CalendarIcon, Edit, ExternalLinkIcon, Square, Check, X, Trash2 } from "lucide-react";
import routes from "../../../modules/routes/Routes";
import { PublicProfileContent } from "../PublicProfilePage";
import { PublicProfileProvider, usePublicProfile } from "../../../hooks/usePublicProfile";
import { useNewspaperRequest } from "../../../hooks/useNewspaperRequest";
import { createPortal } from "react-dom";

export const NewspaperPreview: React.FC<{ 
    title?: string | null, 
    description?: string | null, 
    linkKey?: string | null,
    onEdit?: (title: string | null, description: string | null) => void,
    onDelete?: () => void,
    className?: string,
}> = ({ title, description, linkKey, onEdit, onDelete, className }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(title ?? '');
    const [editDescription, setEditDescription] = useState(description ?? '');

    // Reset edit states when title/description props change
    useEffect(() => {
        setEditTitle(title ?? '');
        setEditDescription(description ?? '');
    }, [title, description]);

    const handleEdit = () => {
        onEdit?.(editTitle, editDescription);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditTitle(title ?? '');
        setEditDescription(description ?? '');
    };

    const handleDelete = () => {
        onDelete?.();
        setIsEditing(false);
    };

    const preview = (
        <div className={`bg-base-200 p-4 rounded-lg w-full max-w-sm ${className ?? ''}`}>
            <div className="font-bold flex items-center h-5">
                <span className="w-3 h-3 bg-gray-300 mr-2"></span>
                {title ?? <div className="skeleton h-5 w-full"></div>}
            </div>
            <div className="text-sm line-clamp-2 mt-1 h-10">
                {description ?? <><div className="skeleton h-4 w-full mb-1"></div><div className="skeleton h-4 w-3/4"></div></>}
            </div>
            <div className="text-sm mt-2">fmz.bz/{linkKey ?? ''}</div>
        </div>
    );

    const editor = (
        <div className="bg-base-200 p-4 rounded-lg w-full max-w-sm space-y-4">
            <Input 
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Title"
                className="w-full"
            />
            <Textarea 
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Description"
                className="w-full"
            />
        </div>
    );

    return (
        <Indicator className={`w-full max-w-sm`}>
            {isEditing ? (
                <>
                    <Indicator.Item className="flex gap-2">
                        <Button 
                            color="ghost" 
                            size="sm" 
                            className="rounded-full p-2"
                            onClick={handleCancel}
                        >
                            <X size={16} />
                        </Button>
                        <Button
                            color="error"
                            size="sm"
                            className="rounded-full p-2"
                            onClick={handleDelete}
                        >
                            <Trash2 size={16} />
                        </Button>
                        <Button 
                            color="primary" 
                            size="sm" 
                            className="rounded-full p-2"
                            onClick={handleEdit}
                        >
                            <Check size={16} />
                        </Button>
                    </Indicator.Item>
                    {editor}
                </>
            ) : (
                <>
                    {onEdit && (
                        <Indicator.Item>
                            <Button 
                                color="primary" 
                                size="sm" 
                                className="rounded-full p-2"
                                onClick={() => setIsEditing(true)}
                            >
                                <Edit size={16} />
                            </Button>
                        </Indicator.Item>
                    )}
                    {preview}
                </>
            )}
        </Indicator>
    );
}

const ConfirmSchedulePost: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    title?: string | null;
    description?: string | null;
    linkKey?: string | null;
}> = ({ isOpen, onClose, title, description, linkKey }) => {
    const [isSuccess, setIsSuccess] = useState(false);
    const [showScamWarning, setShowScamWarning] = useState(true);

    const { requestNewspaperPost } = useNewspaperRequest();

    const handleConfirm = useCallback(async () => {
        await requestNewspaperPost();
        setIsSuccess(true);
    }, [requestNewspaperPost, setIsSuccess]);

    return createPortal(
        <Modal open={isOpen}>
            <Modal.Header className="font-bold">
                {showScamWarning ? 'Important Safety Notice' : !isSuccess ? 'Confirm Post Schedule' : 'Success!'}
            </Modal.Header>

            <Modal.Body>
                {showScamWarning ? (
                    <div className="space-y-4">
                        <div className="bg-warning/20 p-4 rounded-lg">
                            <h3 className="font-bold mb-2">Protect Yourself from Rental Scams</h3>
                            <ul className="list-disc list-inside space-y-2">
                                <li>Never send documents before viewing the apartment in person</li>
                                <li>Never sign a contract without seeing the flat first</li>
                                <li>Never transfer money before signing a legitimate contract</li>
                                <li>Be wary of deals that seem too good to be true</li>
                            </ul>
                        </div>
                        <p className="font-semibold text-center mt-4">
                            Your safety is our priority. Please proceed only after understanding these guidelines.
                        </p>
                    </div>
                ) : !isSuccess ? (
                    <div className="space-y-4">
                        <p>Your membership includes 1 free newspaper post.</p>
                        <p>After requesting, your post will be published in the next 7 days.</p>
                        <p>Would you like to proceed with scheduling your post?</p>
                        
                        <p className="font-bold mt-4">Your post will look like this:</p>
                        <NewspaperPreview title={title} description={description} linkKey={linkKey} />
                    </div>
                ) : (
                    <div className="text-center space-y-4">
                        <div className="flex justify-center">
                            <CalendarIcon className="w-16 h-16 text-success" />
                        </div>
                        <p>Your post has been successfully scheduled!</p>
                    </div>
                )}
            </Modal.Body>

            <Modal.Actions>
                {showScamWarning ? (
                    <>
                        <Button color="ghost" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button color="primary" onClick={() => setShowScamWarning(false)}>
                            I understand
                        </Button>
                    </>
                ) : !isSuccess ? (
                    <>
                        <Button color="ghost" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button color="primary" onClick={handleConfirm}>
                            Confirm
                        </Button>
                    </>
                ) : (
                    <Button color="primary" onClick={onClose}>
                        Close
                    </Button>
                )}
            </Modal.Actions>
        </Modal>,
        document.body
    );
};

const NewspaperPosts: React.FC = () => {
    const { localProfile, handleLocalProfileChange } = useUserProfile();
    const { t } = useTranslation();

    const [isConfirmSchedulePostOpen, setIsConfirmSchedulePostOpen] = useState(false);

    const { publicProfile, setPublished } = usePublicProfile();
    const { newspaperRequest, updateNewspaperRequest } = useNewspaperRequest();

    if (localProfile === undefined) {
        return null; // or return a loading indicator
    }

    const hasRequiredProfileInfo = localProfile.persons?.[0]?.name && localProfile.persons[0].name.trim() !== '';

    const steps = [
        { id: 1, name: 'Profile Information' },
        { id: 2, name: 'Publish Profile' },
        { id: 3, name: 'Schedule Post' },
        { id: 4, name: 'Awaiting review' },
        { id: 5, name: 'On the newspaper' }
    ];

    const currentStep = !hasRequiredProfileInfo ? 1 
        : !publicProfile?.is_published ? 2 
        : newspaperRequest?.status === 'requested' ? 4
        : newspaperRequest?.status === 'published' ? 5
        : 3;
    
    const linkKey = publicProfile?.link_keys?.[publicProfile?.link_keys?.length - 1] ?? '';

    const profileCompletionPercentage = useMemo(() => {
        if (!publicProfile) return 0;

        const requiredFields = [
            !!publicProfile.name?.trim(),
            !!publicProfile.looking_for?.trim(),
            publicProfile.budget_max > 0,
            Array.isArray(publicProfile.preferred_areas) && publicProfile.preferred_areas.length > 0,
            publicProfile.apartment_size_min !== null && publicProfile.apartment_size_min > 0,
            publicProfile.rooms_min > 0,
            !!publicProfile.move_in_date?.trim(),
            publicProfile.min_contract_months !== null && publicProfile.min_contract_months > 0,
            !!publicProfile.about_me?.trim(),
            publicProfile.age !== null && publicProfile.age > 0,
            !!publicProfile.job?.trim()
        ];

        const completedFields = requiredFields.filter(Boolean).length;
        return Math.round((completedFields / requiredFields.length) * 100);
    }, [publicProfile]);

    const handleEdit = useCallback(async (title: string | null, description: string | null) => {
        await updateNewspaperRequest({ title, description });
    }, [updateNewspaperRequest]);

    const handleDelete = useCallback(async () => {
        await updateNewspaperRequest(null);
    }, [updateNewspaperRequest]);

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">
                Newspaper Post
            </h1>

            <ul className="steps w-full mb-8">
                {steps.map((step) => (
                    <li 
                        key={step.id}
                        className={`step ${currentStep >= step.id ? (currentStep === 5 ? 'step-success' : 'step-primary') : ''}`}
                    >
                        {step.name}
                    </li>
                ))}
            </ul>

            <div className="space-y-8">
                <div>
                    <Indicator>
                        <Indicator.Item>
                            <Badge color="primary" size="sm" className="mb-2">{profileCompletionPercentage}% Complete</Badge>
                        </Indicator.Item>
                        <h2 className="text-xl font-semibold mb-4">1. Complete Profile</h2>
                    </Indicator>
                    <p>Please ensure your profile information is up to date before proceeding.</p>
                    <Link to={routes.app.profile.personalProfile.build()}>
                        <Button color="primary" size="sm" className="mt-4">
                            <ExternalLinkIcon size={16} className="mr-1" />
                            Open Profile
                        </Button>
                    </Link>
                </div>

                <div className={`${!hasRequiredProfileInfo ? 'opacity-50 pointer-events-none' : ''}`}>
                    <h2 className="text-xl font-semibold mb-4">2. Publish your profile</h2>
                    <p>This page will be published on the newspaper and will show all necessary information for the landlords.</p>
                    <div className="form-control mt-2">
                        <label className="label cursor-pointer">
                            <span className="label-text">Make my profile public</span>
                            {publicProfile?.is_published === undefined ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <input 
                                    type="checkbox" 
                                    className="toggle toggle-primary"
                                    checked={publicProfile?.is_published ?? false}
                                    onChange={(e) => setPublished(e.target.checked)}
                                    disabled={!hasRequiredProfileInfo}
                                />
                            )}
                        </label>
                    </div>
                    <Link target="_blank" to={routes.app.publicProfile.build(linkKey)}>
                        <Button color="primary" size="sm" className="mt-4">
                            <ExternalLinkIcon size={16} className="mr-1" />
                            Preview your public profile
                        </Button>
                    </Link>
                </div>

                <div className={`${!publicProfile?.is_published ? 'opacity-50 pointer-events-none' : ''}`}>
                    <h2 className="text-xl font-semibold mb-4">3. Schedule Post</h2>
                    <p>Your profile is now public and ready to be featured!</p>
                    <p className="mt-1">Once scheduled, we will review your post. Once reviewed it will be published on the newspaper within the next up to 7 days.</p>
                    <div className="mt-4">
                        <NewspaperPreview 
                            title={newspaperRequest?.title} 
                            description={newspaperRequest?.description} 
                            linkKey={linkKey}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    </div>
                    {/* Change the condition to show different content based on status */}
                    {(newspaperRequest?.status ?? '') === 'pending' ? (
                        <>
                            <Button color="primary" size="sm" className="mt-4" onClick={() => setIsConfirmSchedulePostOpen(true)}>
                                <CalendarIcon size={16} className="mr-1" />
                                Schedule post
                            </Button>
                        </>
                    ) : newspaperRequest?.status === 'requested' ? (
                        <div className="mt-4">
                            <p className="text-success">Your post has been scheduled and is pending review!</p>
                        </div>
                    ) : null}

                    <ConfirmSchedulePost
                        isOpen={isConfirmSchedulePostOpen}
                        onClose={() => setIsConfirmSchedulePostOpen(false)}
                        title={newspaperRequest?.title}
                        description={newspaperRequest?.description}
                        linkKey={linkKey}
                    />

                    {newspaperRequest?.status === 'published' && (
                        <div className="mt-4">
                            <p className="text-success">Great news! Your post has been published on the Badische Zeitung on the {newspaperRequest?.newspaper_date}!</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default () => {
    const { localProfile } = useUserProfile();
    return <PublicProfileProvider userId={localProfile?.id}>
        <NewspaperPosts />
    </PublicProfileProvider>
}
